import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import Cookies from "js-cookie";
import { Lightbulb, Link as LinkIcon, Calendar } from "lucide-react";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { useAlert } from "../../constants/AlertProvider";

export default function Projects() {
  const { candidateId: routeCandidateId } = useParams();
  const [projects, setProjects] = useState([]);
  const [isRecruiterView, setIsRecruiterView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const showAlert = useAlert();

  const formatDate = (dateStr) => {
    if (!dateStr) return "Present";
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const loggedInUserId = Cookies.get("id");
        const targetUserId = routeCandidateId || loggedInUserId;

        if (!targetUserId) {
          throw new Error("Target User ID is missing");
        }

        if (routeCandidateId) {
          setIsRecruiterView(true);
        }

        let userRes;
        if (isRecruiterView) {
          userRes = await axios.get(`/api/candidate-profile/${targetUserId}/`, {
            withCredentials: true,
          });
        } else {
          userRes = await axios.get(`/api/users/${targetUserId}/`, {
            withCredentials: true,
          });
        }

        if (
          userRes.data.applicationids &&
          userRes.data.applicationids.length > 0
        ) {
          const appId = userRes.data.applicationids[0].id;

          const projectResponse = await axios.get(
            `/api/project/?application_id=${appId}`,
            {
              headers: { "X-CSRFToken": Cookies.get("csrftoken") },
              withCredentials: true,
            },
          );

          if (
            projectResponse.data &&
            Array.isArray(projectResponse.data.results)
          ) {
            setProjects(projectResponse.data.results);
          } else {
            throw new Error("Invalid API response format for projects");
          }
        } else {
          setProjects([]);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        showAlert(
          error?.response?.data?.detail || "Failed to fetch projects.",
          "error",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, [showAlert, routeCandidateId, isRecruiterView]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <CircularProgress color="primary" size={30} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {projects.length === 0 ? (
        <div className="text-center py-12 rounded-lg">
          <Lightbulb className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            No Projects Added
          </h3>
          {!isRecruiterView && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Your projects will be displayed here. You can add it by editing
              your profile.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Projects ({projects.length})
          </h2>

          {projects.map((proj) => (
            <div
              key={proj.id}
              className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4"
            >
              <div className="flex items-start space-x-4">
                <Lightbulb className="h-6 w-6 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-1" />
                <div className="w-full">
                  <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100">
                    {proj.name}
                  </h3>
                  <div className="mt-1 w-full flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                    {(proj.start_date || proj.end_date) && (
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1.5" />
                        {formatDate(proj.start_date)} -{" "}
                        {formatDate(proj.end_date)}
                      </span>
                    )}
                    {proj.link && (
                      <a
                        className="flex items-center text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-all duration-200"
                        href={proj.link}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <LinkIcon className="h-4 w-4 mr-1.5" />
                        View
                      </a>
                    )}
                  </div>
                  <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                    {proj.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
